import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  FileMagnifyingGlass, CalendarBlank, MapPin, Eye, Warning, CurrencyEur,
  ShieldWarning, ListNumbers, ArrowRight,
} from "@phosphor-icons/react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return iso; }
};

const fmtEur = (v) => {
  if (v == null || v === 0) return "—";
  try {
    return `${Number(v).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
  } catch { return `${v} €`; }
};

const ESTADO_CLS = {
  pendiente: "bg-slate-100 text-slate-700 border-slate-300",
  en_curso: "bg-brand-soft text-brand border-brand/40",
  preliminar: "bg-blue-50 text-blue-800 border-blue-300",
  terminado: "bg-status-success-bg text-status-success-fg border-status-success-bd",
  anulado: "bg-muted/20 text-muted border-muted/40",
};

export default function PolizaHistoricoTab({ expedienteId }) {
  const [state, setState] = useState({ loading: true, data: null });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true, data: null });
      try {
        const { data } = await api.get(`/expedientes/${expedienteId}/otros-siniestros-poliza`);
        if (!cancelled) setState({ loading: false, data });
      } catch (e) {
        if (!cancelled) setState({
          loading: false,
          data: { total: 0, items: [], error: e?.response?.data?.detail || "Error" },
        });
      }
    })();
    return () => { cancelled = true; };
  }, [expedienteId]);

  if (state.loading) {
    return (
      <div className="h-full flex items-center justify-center text-muted text-sm">
        Consultando histórico de la póliza...
      </div>
    );
  }

  const d = state.data || {};
  const items = d.items || [];

  // Póliza sin valor
  if (d.missing === "numero_poliza") {
    return (
      <EmptyState
        icon={<Warning className="w-8 h-8 text-status-warning-fg" weight="fill" />}
        title="Sin número de póliza"
        message="Este expediente no tiene número de póliza registrado, por lo que no es posible buscar otros siniestros asociados. Rellena el campo en la pestaña Expediente."
      />
    );
  }

  // Resumen agregado
  const totalMinuta = items.reduce((s, x) => s + (x.importe_minuta || 0), 0);
  const totalTasacion = items.reduce((s, x) => s + (x.importe_tasacion || 0), 0);
  const rehusados = items.filter((x) => x.rehuse && x.rehuse !== "ninguno").length;

  return (
    <div className="h-full flex flex-col min-h-0 overflow-y-auto bg-surface" data-testid="poliza-historico-tab">
      {/* Cabecera con contexto y stats */}
      <div className="bg-white border-b border-border px-6 py-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-md bg-brand/10 border border-brand/30 flex items-center justify-center">
            <FileMagnifyingGlass className="w-5 h-5 text-brand" weight="bold" />
          </div>
          <div>
            <div className="font-display font-bold text-[18px] tracking-tight text-foreground">
              Histórico de la póliza
            </div>
            <div className="text-[11.5px] text-muted">
              Otros siniestros registrados para la misma póliza y aseguradora
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <StatCard
            label="Póliza"
            value={d.poliza || "—"}
            mono
            testid="histo-poliza"
          />
          <StatCard
            label="Aseguradora"
            value={d.aseguradora || "—"}
            testid="histo-aseguradora"
          />
          <StatCard
            label="Siniestros previos"
            value={items.length}
            icon={<ListNumbers className="w-4 h-4 text-brand" weight="bold" />}
            testid="histo-count"
          />
          <StatCard
            label={`Rehusados ${rehusados > 0 ? `(${rehusados})` : ""}`}
            value={rehusados > 0 ? rehusados : "0"}
            icon={<ShieldWarning className={`w-4 h-4 ${rehusados > 0 ? "text-status-warning-fg" : "text-muted"}`} weight="bold" />}
            testid="histo-rehusados"
            alert={rehusados > 0}
          />
        </div>

        {items.length > 0 && (
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border text-[12px] text-muted">
            <span>
              <span className="uppercase tracking-widest text-[10px] font-semibold mr-1">Σ Tasación:</span>
              <span className="font-mono font-bold text-foreground">{fmtEur(totalTasacion)}</span>
            </span>
            <span className="text-border">·</span>
            <span>
              <span className="uppercase tracking-widest text-[10px] font-semibold mr-1">Σ Minuta:</span>
              <span className="font-mono font-bold text-foreground">{fmtEur(totalMinuta)}</span>
            </span>
          </div>
        )}
      </div>

      {/* Lista */}
      {items.length === 0 ? (
        <EmptyState
          icon={<CalendarBlank className="w-8 h-8 text-muted" weight="bold" />}
          title="Primer siniestro de esta póliza"
          message={`No hay otros siniestros registrados para la póliza ${d.poliza || "—"} y ${d.aseguradora || "aseguradora desconocida"}. Este es el primer expediente del asegurado.`}
        />
      ) : (
        <div className="flex-1 min-h-0 px-6 py-5">
          <ul className="space-y-2" data-testid="poliza-historico-list">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  onClick={() => navigate(`/review/${it.id}`)}
                  data-testid={`poliza-historico-item-${it.id}`}
                  className="w-full text-left bg-white border border-border hover:border-brand/40 hover:shadow-sm rounded-md transition-all group"
                >
                  <div className="px-4 py-3 flex items-center gap-4">
                    {/* Badge de estado */}
                    <div className={`text-[9.5px] uppercase tracking-wider font-bold px-2 py-1 rounded border ${
                      ESTADO_CLS[it.estado_encargo] || ESTADO_CLS.en_curso
                    } flex-shrink-0`}>
                      {it.estado_encargo}
                    </div>

                    {/* Nº siniestro + causa */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-mono font-bold text-[13px] text-foreground truncate">
                          {it.numero_siniestro || "Sin nº"}
                        </div>
                        {it.rehuse && it.rehuse !== "ninguno" && (
                          <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                            it.rehuse === "parcial"
                              ? "bg-orange-100 text-orange-700 border border-orange-300"
                              : "bg-red-100 text-red-700 border border-red-300"
                          }`}>
                            Rehúse {it.rehuse}
                          </span>
                        )}
                        {it.encargos_count > 1 && (
                          <span className="text-[9.5px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-surface text-muted border border-border">
                            {it.encargos_count} encargos
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted mt-0.5 truncate">
                        {it.causa || "Sin causa"}
                        {it.tipo_encargo && <> · {it.tipo_encargo}</>}
                        {it.perito_codigo && <> · Perito {it.perito_codigo}</>}
                      </div>
                    </div>

                    {/* Fechas */}
                    <div className="hidden md:flex flex-col items-end gap-0.5 text-[11px] text-muted flex-shrink-0 w-[120px]">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" weight="bold" />
                        <span className="font-mono">{fmtDate(it.fecha_siniestro)}</span>
                      </span>
                      {it.fecha_cierre && (
                        <span className="text-status-success-fg inline-flex items-center gap-1">
                          <span className="uppercase tracking-wider text-[9px]">Cerrado</span>
                          <span className="font-mono">{fmtDate(it.fecha_cierre)}</span>
                        </span>
                      )}
                    </div>

                    {/* Importes */}
                    <div className="hidden lg:flex flex-col items-end gap-0.5 text-[11px] flex-shrink-0 w-[100px]">
                      <span className="inline-flex items-center gap-1 font-mono">
                        <CurrencyEur className="w-3 h-3 text-muted" weight="bold" />
                        <span className={it.importe_tasacion ? "text-foreground font-semibold" : "text-muted"}>
                          {fmtEur(it.importe_tasacion)}
                        </span>
                      </span>
                      <span className="text-[10px] text-muted uppercase tracking-wider">tasación</span>
                    </div>

                    <ArrowRight className="w-4 h-4 text-muted group-hover:text-brand transition-colors flex-shrink-0" weight="bold" />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, mono, testid, alert }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-3 ${alert ? "border-status-warning-bd bg-status-warning-bg/30" : "border-border bg-surface"}`}
    >
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`mt-1 text-[15px] font-bold text-foreground truncate ${mono ? "font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, message }) {
  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="flex justify-center mb-4">{icon}</div>
        <div className="font-display font-bold text-[15px] tracking-tight text-foreground mb-2">
          {title}
        </div>
        <div className="text-[12.5px] text-muted">{message}</div>
      </div>
    </div>
  );
}
